import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, FileUp } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, downloadFile, errMsg } from "@/lib/api";
import { TYPE_LABELS } from "@/lib/chem";

const CSV_TEMPLATE = "\ufeffsoru,tip,şık1,şık2,şık3,şık4,doğru_şık,puan,süre,konu,açıklama\n" +
  "Altın elementinin sembolü hangisidir?,çoktan seçmeli,Ag,Au,Al,Pt,2,10,20,Element sembolleri,\n" +
  "H₂O bir bileşiktir.,doğru/yanlış,,,,,doğru,10,15,Element ve bileşik kavramı,\n";

const Report = ({ result }) => (
  <div className="space-y-4" data-testid="import-report">
    <div className="flex gap-4">
      <span className="flex items-center gap-2 text-[#6EE7B7] font-bold" data-testid="import-valid-count"><CheckCircle2 className="h-5 w-5" />{result.valid_count} geçerli</span>
      <span className={`flex items-center gap-2 font-bold ${result.error_count ? "text-red-400" : "text-slate-500"}`} data-testid="import-error-count"><AlertTriangle className="h-5 w-5" />{result.error_count} hatalı</span>
    </div>
    {result.errors.length > 0 && (
      <ul className="rounded-lg border border-red-500/40 bg-red-950/30 p-3 space-y-1 text-sm max-h-40 overflow-y-auto" data-testid="import-errors">
        {result.errors.map((e, i) => <li key={i}><span className="font-mono-x text-red-300">{e.row ? `Satır/Öğe ${e.row}` : "Dosya"}:</span> {e.message}</li>)}
      </ul>
    )}
    {result.preview.length > 0 && (
      <div className="rounded-lg border border-[rgba(71,85,105,0.45)] max-h-64 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-[rgba(15,23,42,0.9)] sticky top-0"><tr className="text-left eyebrow"><th className="p-2">#</th><th className="p-2">Soru</th><th className="p-2">Tip</th><th className="p-2">Doğru</th><th className="p-2">Puan</th></tr></thead>
          <tbody>
            {result.preview.map((q) => (
              <tr key={q.row} className="border-t border-[rgba(71,85,105,0.45)]">
                <td className="p-2 font-mono-x text-slate-500">{q.row}</td>
                <td className="p-2">{q.text}</td>
                <td className="p-2 text-slate-400">{TYPE_LABELS[q.type]}</td>
                <td className="p-2 text-[#6EE7B7]">{q.options.find((o) => o.id === q.correct_option_id)?.text}</td>
                <td className="p-2">{q.points}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

export const ImportDialog = ({ open, onOpenChange, poolId, onDone }) => {
  const [format, setFormat] = useState("csv");
  const [content, setContent] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setContent(""); setResult(null); }
  }, [open]);

  const readFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.name.toLowerCase().endsWith(".json")) setFormat("json");
    if (file.name.toLowerCase().endsWith(".csv")) setFormat("csv");
    setContent(await file.text());
    setResult(null);
  };

  const run = async (commit) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/pools/${poolId}/import`, { format, content, commit });
      setResult(data);
      if (commit) {
        toast.success(`${data.imported} soru içe aktarıldı`);
        onDone();
        onOpenChange(false);
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[rgba(15,23,42,0.55)] border-[rgba(71,85,105,0.45)] max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="import-dialog">
        <DialogHeader><DialogTitle className="font-display text-3xl">Soruları içe aktar</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            {["csv", "json"].map((f) => (
              <button key={f} type="button" onClick={() => { setFormat(f); setResult(null); }} data-testid={`import-format-${f}`}
                className={`px-4 py-2 rounded-lg border font-bold uppercase ${format === f ? "bg-[#FACC15] text-[#05070D] border-[#FACC15]" : "border-[rgba(71,85,105,0.45)]"}`}>{f}</button>
            ))}
            <label className="ml-auto flex items-center gap-2 cursor-pointer px-4 py-2 rounded-lg border border-dashed border-[rgba(100,116,139,0.6)] hover:border-[#FACC15]">
              <FileUp className="h-4 w-4" /> Dosya seç
              <input type="file" accept=".csv,.json,text/csv,application/json" className="hidden" onChange={readFile} data-testid="import-file-input" />
            </label>
            <Button variant="ghost" size="sm" onClick={() => downloadFile(CSV_TEMPLATE, "fchem_sablon.csv", "text/csv;charset=utf-8")} data-testid="import-template-button">CSV şablonu indir</Button>
          </div>
          <p className="text-sm text-slate-400">
            CSV sütunları: soru, tip, şık1, şık2, şık3, şık4, doğru_şık, puan, süre, konu, açıklama. Tip: "çoktan seçmeli" veya "doğru/yanlış"; doğru_şık: 1-4 (doğru/yanlış için "doğru"/"yanlış"). Ayraç virgül veya noktalı virgül olabilir. Eşleştirme ve sınıflandırma soruları yalnızca JSON ile aktarılır (data.pairs: [&#123;left, right&#125;] veya data.categories: [&#123;id, name&#125;] + data.items: [&#123;text, category_id&#125;]).
          </p>
          <Textarea value={content} onChange={(e) => { setContent(e.target.value); setResult(null); }} placeholder="İçeriği buraya yapıştırın veya dosya seçin…"
            className="min-h-40 font-mono-x text-sm" data-testid="import-content-input" />
          {result && <Report result={result} />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => run(false)} disabled={busy || !content.trim()} data-testid="import-preview-button">Önizle</Button>
          <Button onClick={() => run(true)} disabled={busy || !result?.valid_count} className="font-bold" data-testid="import-commit-button">
            {result?.valid_count ? `${result.valid_count} soruyu içe aktar` : "İçe aktar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
